import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useNavigate } from "react-router-dom";
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import '../../styles.css';
import { EffectCoverflow } from 'swiper/modules';

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV



export default function CoverflowSwiper({ onSlideChange }) {
  const [destinations, setDestinations] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch data from the API
    const fetchDestinations = async () => {
      try {
        const response = await fetch(`${API_URL}/web/home/destinations`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        });
        if (!response.ok) throw new Error("API request failed");
        const result = await response.json();
        const apiDestinations = result.data; // Extract the `data` array
        setDestinations(apiDestinations.length > 0 ? apiDestinations : fallbackDestinations); // Use fallback if API returns an empty array
      } catch (error) {
        console.error('Error fetching destinations:', error);
      }
    };

    fetchDestinations();
  }, []);

  const fallbackDestinations = [
    {
      id: 1,
      title: 'Tropical Paradise',
      thumbnail: 'https://images.pexels.com/photos/460672/pexels-photo-460672.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: "Nestled in the heart of the Maldives, this tropical paradise offers stunning turquoise waters, white sandy beaches, and luxurious overwater bungalows. Ideal for honeymooners and those looking for a serene getaway.",
      city: 'Maldives',
      state: 'South Asia',
      country: 'Maldives'
    },
    {
      id: 2,
      title: 'Mountain Adventure',
      thumbnail: 'https://images.pexels.com/photos/345198/pexels-photo-345198.jpeg',
      description: "Explore the rugged beauty of the Swiss Alps, where adventure awaits at every turn. Whether skiing, hiking, or simply enjoying the breathtaking views, this destination is perfect for thrill-seekers and nature lovers alike.",
      city: 'Zermatt',
      state: 'Valais',
      country: 'Switzerland'
    },
    {
      id: 3,
      title: 'City Lights',
      thumbnail: 'https://images.pexels.com/photos/169647/pexels-photo-169647.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: "The dazzling lights of New York City make it one of the most iconic destinations in the world. With world-renowned landmarks, vibrant nightlife, and endless entertainment options, this city never sleeps.",
      city: 'New York City',
      state: 'New York',
      country: 'USA'
    },
    {
      id: 4,
      title: 'Desert Oasis',
      thumbnail: 'https://images.pexels.com/photos/144762/pexels-photo-144762.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: "Venture into the heart of the Sahara Desert, where you'll discover golden sand dunes, ancient oases, and a serene, vast landscape. Perfect for those seeking solitude and adventure in one of the world’s most unique landscapes.",
      city: 'Merzouga',
      state: 'Sahara Desert',
      country: 'Morocco'
    },
    {
      id: 5,
      title: 'Cultural Heritage',
      thumbnail: 'https://images.pexels.com/photos/3870496/pexels-photo-3870496.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: "Explore the rich cultural heritage of Kyoto, Japan. This city is renowned for its beautiful temples, traditional tea houses, and stunning gardens, offering a glimpse into Japan's history and timeless traditions.",
      city: 'Kyoto',
      state: 'Kinki',
      country: 'Japan'
    },
    {
      id: 6,
      title: 'Tropical Escape',
      thumbnail: 'https://images.pexels.com/photos/5608200/pexels-photo-5608200.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: "Discover the stunning beauty of Bali, Indonesia. With its lush landscapes, spiritual culture, and tranquil beaches, Bali is the perfect destination for a peaceful retreat or an adventurous holiday.",
      city: 'Ubud',
      state: 'Bali',
      country: 'Indonesia'
    },
    {
      id: 7,
      title: 'Seaside Retreat',
      thumbnail: 'https://images.pexels.com/photos/30178134/pexels-photo-30178134/free-photo-of-aerial-winter-landscape-of-river-and-snowy-fields.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: "Enjoy the serene and picturesque views of Santorini, Greece. Famous for its stunning white buildings, blue-domed churches, and crystal-clear waters, this destination offers a perfect blend of relaxation and exploration.",
      city: 'Santorini',
      state: 'Cyclades',
      country: 'Greece'
    },
    {
      id: 8,
      title: 'Arctic Wonders',
      thumbnail: 'https://images.pexels.com/photos/207150/pexels-photo-207150.jpeg',
      description: "Journey to the Arctic Circle and marvel at the frozen beauty of Iceland. From the Northern Lights to volcanic landscapes and geothermal hot springs, Iceland offers an unforgettable adventure.",
      city: 'Reykjavik',
      state: 'Capital Region',
      country: 'Iceland'
    },
    {
      id: 9,
      title: 'Historic Charm',
      thumbnail: 'https://images.pexels.com/photos/763268/pexels-photo-763268.jpeg',
      description: "Step back in time as you explore the ancient city of Rome, Italy. With iconic landmarks like the Colosseum, the Roman Forum, and Vatican City, Rome is the perfect destination for history enthusiasts and culture seekers.",
      city: 'Rome',
      state: 'Lazio',
      country: 'Italy'
    },
    {
      id: 10,
      title: 'Oceanic Serenity',
      thumbnail: 'https://images.pexels.com/photos/6775343/pexels-photo-6775343.jpeg?auto=compress&cs=tinysrgb&w=600',
      description: "Relax on the stunning beaches of the Seychelles, an archipelago in the Indian Ocean. Known for its crystal-clear waters, abundant marine life, and lush tropical islands, the Seychelles offers a perfect escape for nature lovers.",
      city: 'Mahe',
      state: 'Seychelles Islands',
      country: 'Seychelles'
    },
  ];

  // Handle slide change
  const handleSlideChange = (swiper) => {
    const activeIndex = swiper.realIndex;
    if (destinations.length > 0 && activeIndex >= 0 && activeIndex < destinations.length) {
      const activeSlideData = destinations[activeIndex];
      if (onSlideChange) {
        onSlideChange(activeSlideData);
      }
    } else {
      console.error("Invalid slide index", activeIndex);
    }
  };

  // Handle card click
  const handleCardClick = (swiper) => {
    const activeIndex = swiper.realIndex;
    if (destinations.length > 0 && activeIndex >= 0 && activeIndex < destinations.length) {
      navigate(`/pg/destinations/explore/${destinations[activeIndex].id}`);
    } else {
      console.error("Invalid slide click - no data", activeIndex);
    }
  };

  return (
    <div className="swiper-container-wrapper">
      {destinations.length > 0 && (
        <Swiper
          effect={'coverflow'}
          grabCursor={true}
          centeredSlides={true}
          slidesPerView={'auto'} // Responsive slides per view
          spaceBetween={20}
          coverflowEffect={{
            rotate: 10,
            stretch: 0,
            depth: 100,
            modifier: 1,
            slideShadows: false,
          }}
          modules={[EffectCoverflow]}
          className="mySwiper"
          loop={true}
          onSlideChange={handleSlideChange}
          onClick={handleCardClick}
          breakpoints={{
            // Responsive breakpoints
            320: { // Mobile
              slidesPerView: 1,
              spaceBetween: 10,
              coverflowEffect: {
                rotate: 10,
                stretch: 0,
                depth: 50,
                modifier: 1,
              },
            },
            768: { // Tablet
              slidesPerView: 2,
              spaceBetween: 20,
              coverflowEffect: {
                rotate: 10,
                stretch: 0,
                depth: 100,
                modifier: 1,
              },
            },
            1024: { // Desktop
              slidesPerView: 3,
              spaceBetween: 30,
              coverflowEffect: {
                rotate: 10,
                stretch: 0,
                depth: 150,
                modifier: 1,
              },
            },
          }}
        >
          {destinations.map((destination) => (
            <SwiperSlide key={destination.id} className="slide">
              <div className="slide-content">
                <img src={destination.thumbnail} alt={destination.title} className="destination-image" />
                <div className="overlay-text">{destination.title}</div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}